/* ==========================================================================
   Therapin Quiz V2 — <tr-quiz-v2>
   A single-page intake funnel: lander → branching questions → email gate →
   named result profile. All content comes from theme blocks; this file only
   decides which screen is on, which questions are eligible, and which result
   profile the answers add up to.
   ========================================================================== */

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** ?qv2debug=1 shows a state panel. Guarded by the query string, so a normal
 *  visitor can never see it. */
const DEBUG = (() => {
  try {
    return new URLSearchParams(window.location.search).has('qv2debug');
  } catch (e) {
    return false;
  }
})();

/** "a, b ,c" → ['a','b','c'] (lower-cased, blanks dropped) */
function parseTags(value) {
  if (!value) return [];
  return value
    .split(',')
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * Split an element's text into per-character spans kept inside per-word
 * wrappers, so the blur reveal never breaks a word across two lines.
 */
function splitText(el) {
  if (el.dataset.qv2Split === 'done') return Array.from(el.querySelectorAll('.tr-qv2-char'));

  const source = el.textContent;
  el.dataset.qv2SplitText = source;
  const frag = document.createDocumentFragment();
  const chars = [];

  source.split(/(\s+)/).forEach((token) => {
    if (!token) return;
    if (/^\s+$/.test(token)) {
      frag.appendChild(document.createTextNode(token));
      return;
    }
    const word = document.createElement('span');
    word.className = 'tr-qv2-word';
    for (const ch of token) {
      const span = document.createElement('span');
      span.className = 'tr-qv2-char';
      span.textContent = ch;
      word.appendChild(span);
      chars.push(span);
    }
    frag.appendChild(word);
  });

  el.textContent = '';
  el.appendChild(frag);
  el.dataset.qv2Split = 'done';
  return chars;
}

/** The Framer-style blur-in: characters fade up out of a 5px blur, staggered. */
function revealText(el, { stagger = 18, duration = 620, blur = 5 } = {}) {
  let chars;
  try {
    chars = splitText(el);
  } catch (e) {
    return; // leave the original text alone
  }
  if (REDUCED || typeof el.animate !== 'function') return;

  chars.forEach((char, i) => {
    try {
      if (typeof char.getAnimations === 'function') {
        char.getAnimations().forEach((a) => a.cancel());
      }
      char.animate(
        [
          { opacity: 0.001, filter: `blur(${blur}px)`, transform: 'translateY(0.14em)' },
          { opacity: 1, filter: 'blur(0px)', transform: 'translateY(0)' },
        ],
        {
          duration,
          delay: i * stagger,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          fill: 'both',
        }
      );
    } catch (e) {
      /* decoration only — the character stays visible */
    }
  });
}

/** Block-level fade-up used for everything that isn't split text. */
/**
 * Block-level fade-up. Driven by the Web Animations API rather than a CSS
 * class: an animation's fill state wins over author CSS, so the element ends
 * up visible even if a stale cached stylesheet still parks it at opacity 0.
 * Visibility must never depend on the stylesheet being fresh.
 */
function revealBlocks(root, startDelay = 0) {
  const items = Array.from(root.querySelectorAll('[data-qv2-fade]'));
  items.forEach((item, i) => {
    item.classList.add('is-in');

    if (REDUCED || typeof item.animate !== 'function') {
      item.style.opacity = '1';
      return;
    }
    try {
      if (typeof item.getAnimations === 'function') {
        item.getAnimations().forEach((a) => a.cancel());
      }
      item.animate(
        [
          { opacity: 0, transform: 'translateY(10px)', filter: 'blur(4px)' },
          { opacity: 1, transform: 'none', filter: 'blur(0px)' },
        ],
        {
          duration: 600,
          delay: startDelay + i * 70,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          fill: 'both',
        }
      );
    } catch (e) {
      item.style.opacity = '1';
    }
  });
}

class TherapinQuizV2 extends HTMLElement {
  connectedCallback() {
    if (this.dataset.qv2Ready === 'true') return;
    this.dataset.qv2Ready = 'true';

    this.storageKey = `tr-qv2:${this.dataset.qv2Key || this.id}`;

    this.screens = Array.from(this.querySelectorAll('[data-qv2-screen]'));
    this.lander = this.screens.find((s) => s.dataset.qv2Screen === 'lander') || null;
    this.questions = this.screens.filter((s) => s.dataset.qv2Screen === 'question');
    this.emailScreen = this.screens.find((s) => s.dataset.qv2Screen === 'email') || null;
    this.results = this.screens.filter((s) => s.dataset.qv2Screen === 'result');

    this.progress = this.querySelector('[data-qv2-progress]');
    this.progressFill = this.querySelector('[data-qv2-progress-fill]');
    this.stepLabel = this.querySelector('[data-qv2-step-label]');
    this.prevBtn = this.querySelector('[data-qv2-prev]');
    this.nextBtn = this.querySelector('[data-qv2-next]');
    this.footer = this.querySelector('[data-qv2-footer]');
    this.continueBtn = this.querySelector('[data-qv2-continue]');
    this.footnote = this.querySelector('[data-qv2-footnote]');
    // The glow trace is markup, not CSS, so keep a copy to carry across the
    // button/link swaps the result screen does.
    this.traceMarkup = this.continueBtn?.querySelector('.tr-qv2-trace')?.outerHTML || '';

    // Original markup of anything carrying a [product] token, so the token can
    // be re-substituted if the visitor goes back and changes their answer.
    this.tokenCache = new WeakMap();
    this.resultTail = this.querySelector('[data-qv2-result-tail]');

    // Snapshot each result exactly as the server rendered it, before anything
    // touches the DOM. If a result later turns up empty we restore from this
    // rather than showing the visitor a blank screen — and the snapshot tells
    // us whether it arrived empty or was emptied afterwards.
    this.resultSnapshots = new Map();
    this.results.forEach((r) => {
      const html = r.innerHTML;
      this.resultSnapshots.set(r, html);
      if (!r.textContent.trim()) {
        this.debugNote(`result "${r.dataset.qv2Handle}" arrived EMPTY from the server`);
      }
    });

    this.autoAdvance = this.dataset.qv2Autoadvance === 'true';
    this.resultMode = this.dataset.qv2ResultMode || 'inline';
    this.resultUrl = this.dataset.qv2ResultUrl || '';

    /** questionId → Set of option values */
    this.answers = new Map();
    /** The screen elements the visitor has actually passed through. */
    this.trail = [];
    this.current = null;
    this.chosenResult = null;

    this.restore();
    this.bind();

    if (DEBUG) {
      window.addEventListener('error', (e) =>
        this.debugNote(`JS ERROR: ${e.message} @ ${(e.filename || '').split('/').pop()}:${e.lineno}`)
      );
    }

    // In the theme editor, jump straight to whichever block is selected.
    // The editor re-renders the whole section on every edit, which builds a new
    // element. Re-apply whichever block was selected so the preview does not
    // snap back to the lander mid-edit.
    const selected =
      editorSelection && this.screens.find((s) => s.dataset.qv2Id === editorSelection);

    if (selected) {
      this.previewScreen(selected);
    } else {
      this.show(this.trail[this.trail.length - 1] || this.lander || this.questions[0], {
        animate: false,
      });
    }
  }

  /* ------------------------------------------------------------- wiring */

  bind() {
    this.addEventListener('click', (event) => {
      const choice = event.target.closest('[data-qv2-choice]');
      if (choice && this.contains(choice)) {
        this.toggleChoice(choice);
        return;
      }
      const start = event.target.closest('[data-qv2-start]');
      if (start && this.contains(start)) {
        event.preventDefault();
        this.next();
      }
    });

    this.prevBtn?.addEventListener('click', () => this.prev());
    this.nextBtn?.addEventListener('click', () => this.next());
    this.continueBtn?.addEventListener('click', (event) => {
      if (this.continueBtn.tagName === 'A') return; // result CTA is a real link
      event.preventDefault();
      this.next();
    });

    this.emailForm = this.emailScreen?.querySelector('.tr-qv2-email-form') || null;
    this.emailForm?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.submitEmail();
    });
    this.emailScreen?.querySelector('[data-qv2-skip]')?.addEventListener('click', () => {
      this.goToResult();
    });

    this.keyHandler = (event) => {
      if (!this.isConnected) return;
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (event.key === 'ArrowLeft' || event.key === 'Escape') {
        event.preventDefault();
        this.prev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next();
      } else if (event.key === 'Enter' && tag !== 'BUTTON' && tag !== 'A') {
        event.preventDefault();
        this.next();
      }
    };
    document.addEventListener('keydown', this.keyHandler);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.keyHandler);
  }

  /** Theme editor: `shopify:block:select` deep-links straight to a block. */
  /**
   * Jump the preview to one screen, fully dressed. The theme editor uses this
   * when a block is selected; a result reached this way has no answers behind
   * it, so every conditional line is shown rather than resolved.
   */
  previewScreen(screen) {
    if (!screen) return;
    if (screen.dataset.qv2Screen === 'result') {
      try {
        const override = this.ctaOverride();
        this.dressResult(screen, override, true);
        this.applyResultCta(screen, override);
      } catch (e) {
        /* a half-dressed result still beats a blank screen */
      }
    }
    this.show(screen, { animate: false, push: false });
  }

  /* -------------------------------------------------------------- state */

  restore() {
    try {
      const raw = sessionStorage.getItem(this.storageKey);
      if (!raw) return;
      const saved = JSON.parse(raw);
      Object.entries(saved.answers || {}).forEach(([qid, values]) => {
        this.answers.set(qid, new Set(values));
        const screen = this.questions.find((q) => q.dataset.qv2Id === qid);
        if (!screen) return;
        screen.querySelectorAll('[data-qv2-choice]').forEach((btn) => {
          btn.setAttribute('aria-pressed', values.includes(btn.dataset.qv2Value) ? 'true' : 'false');
        });
        this.syncMicro(screen);
      });
    } catch (e) {
      /* a blocked or full sessionStorage just means we start fresh */
    }
  }

  persist() {
    try {
      const answers = {};
      this.answers.forEach((set, qid) => {
        answers[qid] = Array.from(set);
      });
      sessionStorage.setItem(this.storageKey, JSON.stringify({ answers }));
    } catch (e) {
      /* non-fatal */
    }
  }

  /** Every tag carried by every option the visitor has selected so far. */
  get tags() {
    const tags = new Set();
    this.questions.forEach((q) => {
      const picked = this.answers.get(q.dataset.qv2Id);
      if (!picked) return;
      q.querySelectorAll('[data-qv2-choice]').forEach((btn) => {
        if (picked.has(btn.dataset.qv2Value)) parseTags(btn.dataset.qv2Tags).forEach((t) => tags.add(t));
      });
    });
    return tags;
  }

  /** Human-readable labels of everything picked, for the result page. */
  get pickedLabels() {
    const labels = [];
    this.questions.forEach((q) => {
      const picked = this.answers.get(q.dataset.qv2Id);
      if (!picked) return;
      q.querySelectorAll('[data-qv2-choice]').forEach((btn) => {
        if (picked.has(btn.dataset.qv2Value)) labels.push(btn.dataset.qv2Label || btn.textContent.trim());
      });
    });
    return labels;
  }

  /* ---------------------------------------------------------- branching */

  isEligible(question) {
    const tags = this.tags;
    const show = parseTags(question.dataset.qv2Showif);
    const hide = parseTags(question.dataset.qv2Hideif);
    if (hide.length && hide.some((t) => tags.has(t))) return false;
    if (!show.length) return true;
    const mode = question.dataset.qv2ShowifMode === 'all' ? 'all' : 'any';
    return mode === 'all' ? show.every((t) => tags.has(t)) : show.some((t) => tags.has(t));
  }

  get eligibleQuestions() {
    return this.questions.filter((q) => this.isEligible(q));
  }

  /* ----------------------------------------------------------- answering */

  toggleChoice(button) {
    const screen = button.closest('[data-qv2-screen]');
    const qid = screen.dataset.qv2Id;
    const multi = screen.dataset.qv2Multi === 'true';
    const value = button.dataset.qv2Value;
    const picked = this.answers.get(qid) || new Set();

    if (multi) {
      if (picked.has(value)) picked.delete(value);
      else picked.add(value);
    } else {
      picked.clear();
      picked.add(value);
    }

    if (picked.size) this.answers.set(qid, picked);
    else this.answers.delete(qid);

    screen.querySelectorAll('[data-qv2-choice]').forEach((btn) => {
      btn.setAttribute('aria-pressed', picked.has(btn.dataset.qv2Value) ? 'true' : 'false');
    });

    this.persist();
    this.syncMicro(screen);
    this.syncControls();

    if (!multi && this.autoAdvance) {
      window.setTimeout(() => this.next(), 260);
    }
  }

  /** Show the "you're in the majority…" line once the question is answered. */
  syncMicro(screen) {
    const micro = screen.querySelector('[data-qv2-micro]');
    if (!micro) return;
    const answered = (this.answers.get(screen.dataset.qv2Id) || new Set()).size > 0;
    if (answered && micro.hidden) {
      micro.hidden = false;
      micro.setAttribute('data-qv2-fade', '');
      micro.style.setProperty('--qv2-fade-delay', '0ms');
      micro.classList.remove('is-in');
      void micro.offsetWidth;
      micro.classList.add('is-in');
    } else if (!answered) {
      micro.hidden = true;
    }
  }

  isAnswered(screen) {
    if (screen?.dataset.qv2Screen !== 'question') return true;
    if (screen.dataset.qv2Required === 'false') return true;
    return (this.answers.get(screen.dataset.qv2Id) || new Set()).size > 0;
  }

  /* ---------------------------------------------------------- navigation */

  next() {
    const screen = this.current;
    if (!screen) return;

    if (screen.dataset.qv2Screen === 'question' && !this.isAnswered(screen)) return;

    if (screen.dataset.qv2Screen === 'email') {
      this.submitEmail();
      return;
    }

    if (screen.dataset.qv2Screen === 'result') return;

    const queue = this.eligibleQuestions;
    let target = null;

    if (screen.dataset.qv2Screen === 'lander') {
      target = queue[0];
    } else {
      const index = queue.indexOf(screen);
      target = index > -1 ? queue[index + 1] : queue[0];
    }

    if (target) {
      this.show(target);
      return;
    }

    if (this.emailScreen) this.show(this.emailScreen);
    else this.goToResult();
  }

  prev() {
    if (this.trail.length < 2) return;
    this.trail.pop();
    const target = this.trail[this.trail.length - 1];
    this.show(target, { push: false });
  }

  show(screen, { animate = true, push = true } = {}) {
    if (!screen) return;

    this.screens.forEach((s) => s.classList.toggle('is-active', s === screen));
    this.current = screen;

    if (push) {
      const existing = this.trail.indexOf(screen);
      if (existing > -1) this.trail.length = existing + 1;
      else this.trail.push(screen);
    }

    this.syncControls();
    window.scrollTo({ top: 0, behavior: animate && !REDUCED ? 'smooth' : 'auto' });

    // Decoration only. If any of it fails the screen still has to be readable.
    const headline = screen.querySelector('[data-qv2-reveal]');
    try {
      if (headline) revealText(headline, { stagger: Number(this.dataset.qv2Stagger || 18) });
      revealBlocks(screen, headline ? 240 : 0);
    } catch (e) {
      screen.querySelectorAll('[data-qv2-fade]').forEach((el) => {
        el.style.opacity = '1';
      });
      screen.querySelectorAll('.tr-qv2-char').forEach((el) => {
        el.style.opacity = '1';
        el.style.filter = 'none';
      });
    }

    this.debugRender();

    this.dispatchEvent(
      new CustomEvent('tr-quiz-v2:step', {
        bubbles: true,
        detail: { screen: screen.dataset.qv2Screen, id: screen.dataset.qv2Id || null },
      })
    );
  }

  syncControls() {
    const screen = this.current;
    if (!screen) return;
    const kind = screen.dataset.qv2Screen;

    // progress bar + step counter
    const queue = this.eligibleQuestions;
    const total = queue.length + (this.emailScreen ? 1 : 0) + 1; // + result
    let index = 0;
    if (kind === 'question') index = queue.indexOf(screen) + 1;
    else if (kind === 'email') index = queue.length + 1;
    else if (kind === 'result') index = total;

    if (this.progress) this.progress.hidden = kind === 'lander';
    if (this.progressFill) this.progressFill.style.width = `${Math.round((index / total) * 100)}%`;
    if (this.stepLabel) {
      this.stepLabel.textContent =
        kind === 'question' ? `${index} / ${queue.length}` : kind === 'result' ? '' : '';
    }

    if (this.prevBtn) this.prevBtn.disabled = this.trail.length < 2;
    if (this.nextBtn) this.nextBtn.disabled = kind === 'result' || !this.isAnswered(screen);

    // sticky footer button
    if (!this.footer || !this.continueBtn) return;

    if (kind === 'result') {
      this.footer.hidden = false;
      return;
    }

    // Coming back off the result screen: the CTA there is a real link, so swap
    // it back for the button that drives the flow.
    if (this.continueBtn.tagName === 'A') {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = this.continueBtn.className;
      button.setAttribute('data-qv2-continue', '');
      button.innerHTML =
        this.traceMarkup +
        '<span data-qv2-continue-label></span><span class="tr-qv2-cta__arrow" aria-hidden="true">&rarr;</span>';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.next();
      });
      this.continueBtn.replaceWith(button);
      this.continueBtn = button;
      if (this.footnote) this.footnote.hidden = true;
    }

    this.footer.hidden = kind === 'lander' && this.dataset.qv2LanderCta === 'inline';
    this.continueBtn.disabled = !this.isAnswered(screen);

    const labels = {
      lander: this.dataset.qv2LabelStart,
      question: this.dataset.qv2LabelContinue,
      email: this.dataset.qv2LabelEmail,
    };
    const label = labels[kind];
    const span = this.continueBtn.querySelector('[data-qv2-continue-label]');
    if (label && span) span.textContent = label;
  }

  /* --------------------------------------------------------- email gate */

  submitEmail() {
    const form = this.emailForm;
    if (!form) {
      this.goToResult();
      return;
    }

    const input = form.querySelector('[data-qv2-email-input]');
    const error = this.emailScreen.querySelector('[data-qv2-error]');
    const value = (input?.value || '').trim();

    if (!value || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
      if (error) error.hidden = false;
      input?.focus();
      return;
    }
    if (error) error.hidden = true;

    const result = this.pickResult();
    const tagsField = form.querySelector('[data-qv2-tags-field]');
    if (tagsField) {
      const profile = result?.dataset.qv2Handle || '';
      tagsField.value = ['quiz-v2', profile ? `profile-${profile}` : '', ...this.tags]
        .filter(Boolean)
        .join(',');
    }

    // Fire and forget — the visitor should never wait on Shopify to see
    // their result, and a failed subscribe must not trap them here.
    try {
      fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
      }).catch(() => {});
    } catch (e) {
      /* non-fatal */
    }

    this.dispatchEvent(
      new CustomEvent('tr-quiz-v2:lead', {
        bubbles: true,
        detail: { email: value, tags: Array.from(this.tags), profile: result?.dataset.qv2Handle || null },
      })
    );

    this.goToResult(result);
  }

  /* -------------------------------------------------------------- result */

  /**
   * An answer can override the profile's button (the scent fork picks the
   * product). Questions run in order, so the last one answered wins.
   */
  ctaOverride() {
    let override = null;
    this.questions.forEach((q) => {
      const picked = this.answers.get(q.dataset.qv2Id);
      if (!picked) return;
      q.querySelectorAll('[data-qv2-choice]').forEach((btn) => {
        if (picked.has(btn.dataset.qv2Value) && btn.dataset.qv2Cta) {
          override = {
            url: btn.dataset.qv2Cta,
            label: btn.dataset.qv2CtaLabel || '',
            variant: btn.dataset.qv2CtaVariant || '',
            name: btn.dataset.qv2CtaName || '',
          };
        }
      });
    });
    return override;
  }

  pickResult() {
    if (!this.results.length) return null;
    const tags = this.tags;
    let best = null;
    let bestScore = -1;

    this.results.forEach((result) => {
      const match = parseTags(result.dataset.qv2Match);
      const score = match.reduce((sum, t) => sum + (tags.has(t) ? 1 : 0), 0);
      const priority = Number(result.dataset.qv2Priority || 0);
      const weighted = score * 1000 + priority;
      if (score > 0 && weighted > bestScore) {
        bestScore = weighted;
        best = result;
      }
    });

    if (best) return best;
    return this.results.find((r) => r.dataset.qv2Default === 'true') || this.results[0];
  }

  /**
   * Resolve everything on a result page that depends on the answers: which
   * conditional lines belong on it, any [product] token, and the shared
   * disclaimer. Split out because the theme editor jumps straight to a result
   * block without going through the quiz, and an undressed result looks broken.
   */
  dressResult(result, override, preview = false) {
    const tags = this.tags;

    result.querySelectorAll('[data-qv2-note]').forEach((note) => {
      if (preview) {
        note.hidden = false;
        return;
      }
      const show = parseTags(note.dataset.qv2NoteShowif);
      const hide = parseTags(note.dataset.qv2NoteHideif);
      let visible = show.length ? show.some((t) => tags.has(t)) : true;
      if (hide.length && hide.some((t) => tags.has(t))) visible = false;
      note.hidden = !visible;
    });

    const productName = override?.name || result.dataset.qv2ProductName || '';
    result.querySelectorAll('[data-qv2-token]').forEach((el) => {
      if (!this.tokenCache.has(el)) this.tokenCache.set(el, el.innerHTML);
      const template = this.tokenCache.get(el);
      el.innerHTML = productName ? template.split('[product]').join(productName) : template;
    });

    // The disclaimer and the below-fold block belong on every result page, so
    // they are rendered once and moved to whichever one is showing.
    if (!this.resultTail) return;
    const inner = result.querySelector('.tr-qv2-inner');
    if (!inner) return;
    if (this.resultTailNodes) {
      this.resultTailNodes.forEach((node) => inner.appendChild(node));
    } else {
      const fragment = this.resultTail.content.cloneNode(true);
      this.resultTailNodes = Array.from(fragment.children);
      inner.appendChild(fragment);
    }
  }

  /** Swap the sticky footer for a profile's own call to action. */
  applyResultCta(result, override) {
    let cta = override?.url || result.dataset.qv2Cta;
    const ctaLabel = override?.label || result.dataset.qv2CtaLabel;

    // A profile can insist on a quantity the answer's link doesn't carry —
    // the partner two-pack, whichever product the scent answer chose.
    const forceQty = Number(result.dataset.qv2ForceQty || 1);
    if (forceQty > 1 && override?.variant) cta = `/cart/${override.variant}:${forceQty}`;

    if (this.continueBtn && cta) {
      const link = document.createElement('a');
      link.className = this.continueBtn.className;
      link.href = cta;
      link.setAttribute('data-qv2-continue', '');
      link.innerHTML = `${this.traceMarkup}<span data-qv2-continue-label>${ctaLabel || 'Continue'}</span><span class="tr-qv2-cta__arrow" aria-hidden="true">&rarr;</span>`;
      this.continueBtn.replaceWith(link);
      this.continueBtn = link;
    }
    if (this.footnote) {
      this.footnote.textContent = result.dataset.qv2Footnote || '';
      this.footnote.hidden = !result.dataset.qv2Footnote;
    }
  }

  goToResult(precomputed) {
    const result = precomputed || this.pickResult();
    this.chosenResult = result;

    const detail = {
      profile: result?.dataset.qv2Handle || null,
      name: result?.dataset.qv2Name || null,
      tags: Array.from(this.tags),
      picks: this.pickedLabels,
    };

    this.dispatchEvent(new CustomEvent('tr-quiz-v2:complete', { bubbles: true, detail }));

    if (this.resultMode === 'redirect' && this.resultUrl) {
      const url = new URL(this.resultUrl, window.location.origin);
      if (detail.profile) url.searchParams.set('profile', detail.profile);
      if (detail.tags.length) url.searchParams.set('signals', detail.tags.join(','));
      window.location.assign(url.toString());
      return;
    }

    if (!result) {
      this.debugNote('pickResult() matched nothing — no result screen to show');
      return;
    }

    // Everything below is dressing. A failure in any of it must not stop the
    // screen being shown, so the whole tail is guarded and show() always runs.
    try {
      const override = this.ctaOverride();
      this.dressResult(result, override);
      this.decorateResult(result, override);
    } catch (e) {
      this.debugNote(`dressing the result threw: ${e && e.message}`);
    }

    // A result screen with no content is never an acceptable thing to show.
    // If one turns up empty, say so on the page and keep the offer reachable,
    // rather than handing the visitor a blank screen.
    this.ensureResultHasContent(result);

    this.show(result);
  }

  ensureResultHasContent(result) {
    let inner = result.querySelector('.tr-qv2-inner');
    if (inner && inner.textContent.trim()) return;

    // Something emptied it after load. Put the server's own markup back.
    const snapshot = this.resultSnapshots?.get(result);
    if (snapshot && snapshot.replace(/<[^>]+>/g, '').trim()) {
      result.innerHTML = snapshot;
      result.removeAttribute('hidden');
      this.debugNote(`restored "${result.dataset.qv2Handle}" from its boot snapshot`);
      inner = result.querySelector('.tr-qv2-inner');
      if (inner && inner.textContent.trim()) return;
    }

    const handle = result.dataset.qv2Handle || '(no profile id)';
    const origin = snapshot && snapshot.trim() ? 'emptied after load' : 'empty from the server';
    const box = inner || document.createElement('div');
    if (!inner) {
      box.className = 'tr-qv2-inner';
      result.appendChild(box);
    }
    result.removeAttribute('hidden');
    box.innerHTML = [
      '<p class="tr-qv2-result__name">Therapin</p>',
      '<h2 class="tr-qv2-h2">Your result is ready.</h2>',
      '<div class="tr-qv2-result__body"><p>Based on your answers, the strip below is the right ',
      'one for you. Tap through to see how it works.</p></div>',
      DEBUG
        ? `<div class="tr-qv2-result__body" style="opacity:.5;font-size:.75em"><p>profile: ${handle}<br>block: ${result.dataset.qv2Id || '?'}<br>cause: ${origin}</p></div>`
        : '',
    ].join('');
    this.debugNote(`result "${handle}" rendered with no content`);
  }

  /** Picks list, answer count and the profile's call to action. */
  decorateResult(result, override) {

    // Mirror the visitor's own words back at them on the result screen.
    const picksList = result.querySelector('[data-qv2-picks]');
    if (picksList) {
      picksList.innerHTML = '';
      this.pickedLabels.forEach((label) => {
        const li = document.createElement('li');
        li.textContent = label;
        picksList.appendChild(li);
      });
      picksList.hidden = this.pickedLabels.length === 0;
    }

    const count = result.querySelector('[data-qv2-pick-count]');
    if (count) count.textContent = String(this.pickedLabels.length);

    this.applyResultCta(result, override);
  }

  /* --------------------------------------------------------------- debug */

  debugNote(message) {
    if (!DEBUG) return;
    this.debugNotes = this.debugNotes || [];
    this.debugNotes.push(message);
    this.debugRender();
  }

  debugRender() {
    if (!DEBUG) return;
    if (!this.debugPanel) {
      this.debugPanel = document.createElement('div');
      this.debugPanel.setAttribute('style', [
        'position:fixed', 'inset:auto 8px 8px 8px', 'z-index:2147483647',
        'max-height:46vh', 'overflow:auto', 'padding:10px 12px',
        'background:#111', 'color:#0f0', 'font:11px/1.45 ui-monospace,Menlo,monospace',
        'border:1px solid #0f0', 'white-space:pre-wrap', 'text-align:left',
      ].join(';'));
      document.body.appendChild(this.debugPanel);
    }
    const kinds = {};
    this.screens.forEach((s) => {
      kinds[s.dataset.qv2Screen] = (kinds[s.dataset.qv2Screen] || 0) + 1;
    });
    const answered = [];
    this.answers.forEach((set, qid) => answered.push(`${qid}=${Array.from(set).join('|')}`));
    const active = this.screens.filter((s) => s.classList.contains('is-active'));
    const picked = this.pickResult();

    this.debugPanel.textContent = [
      `screens      ${this.screens.length} ${JSON.stringify(kinds)}`,
      `results      ${this.results.length}`,
      `current      ${this.current?.dataset.qv2Screen || 'none'} ${this.current?.dataset.qv2Handle || ''}`,
      `is-active    ${active.length} [${active.map((s) => s.dataset.qv2Screen).join(', ')}]`,
      `trail        ${this.trail.map((s) => s.dataset.qv2Screen).join(' > ') || '(empty)'}`,
      `answers      ${answered.join('  ') || '(none)'}`,
      `tags         ${Array.from(this.tags).join(', ') || '(none)'}`,
      `eligible Qs  ${this.eligibleQuestions.length}`,
      `pickResult   ${picked ? picked.dataset.qv2Handle : 'NOTHING'}`,
      `resultMode   ${this.resultMode}`,
      `notes        ${(this.debugNotes || []).join(' | ') || '(none)'}`,
    ].join('\n');
  }
}

/* ---------------------------------------------------------------- editor ---
   These listeners live at module scope on purpose. Registering them inside
   connectedCallback lost the race: the editor inserts the re-rendered section
   and fires the select event before the custom element upgrades, so the click
   landed on nothing and the preview stayed on the lander.
   --------------------------------------------------------------------------- */

/** data-qv2-id of the screen the editor currently has selected. */
let editorSelection = null;

function handleEditorSelect(event) {
  const target = event.target;
  if (!target || typeof target.closest !== 'function') return;

  const screen = target.closest('[data-qv2-screen]');
  if (!screen) return;

  const quiz = screen.closest('tr-quiz-v2');
  if (!quiz) return;

  editorSelection = screen.dataset.qv2Id || null;

  // The element may not have upgraded yet on a freshly rendered section; its
  // own connectedCallback reads editorSelection and catches up.
  if (typeof quiz.previewScreen === 'function') quiz.previewScreen(screen);
}

if (window.Shopify?.designMode) {
  document.addEventListener('shopify:block:select', handleEditorSelect);
  document.addEventListener('shopify:block:deselect', () => {
    editorSelection = null;
  });
}

if (!customElements.get('tr-quiz-v2')) {
  customElements.define('tr-quiz-v2', TherapinQuizV2);
}

export default TherapinQuizV2;
